/**
 * Package Enforcement Context — Phase 2F: API-Driven
 * REMOVED: sessionStorage persistence, initialProviderSubscriptions mock data
 * Backend APIs used: GET/PUT /api/provider-enforcement/:providerId, GET /api/packages
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../utils/api';


const PackageEnforcementContext = createContext();

export const usePackageEnforcement = () => {
  const ctx = useContext(PackageEnforcementContext);
  if (!ctx) throw new Error('usePackageEnforcement must be used within PackageEnforcementProvider');
  return ctx;
};

export const PackageEnforcementProvider = ({ children }) => {
  const [subscriptions, setSubscriptions] = useState({});
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all subscriptions and packages from backend.
  // P7-CLEANUP-6: skip when there's no auth token (anonymous landing/search pages)
  // and downgrade transient auth errors to console.debug to keep dev console clean.
  const fetchData = useCallback(async () => {
    const token = (typeof window !== 'undefined' && (

      localStorage.getItem('treatx_token') ||
      localStorage.getItem('clinic_token') ||
      localStorage.getItem('patient_token') ||
      localStorage.getItem('rbac_token')
    )) || null;
    if (!token) {
      // Anonymous mode — these endpoints would 401/404. Bail early.
      return;
    }
    setLoading(true);
    try {
      const [subRes, pkgRes] = await Promise.all([
        api.get(`/provider-enforcement`),
        api.get(`/packages`),
      ]);
      const subData = subRes.data;
      const pkgData = pkgRes.data;

      if (subData.success) {
        const subMap = {};
        for (const sub of subData.subscriptions) {
          subMap[sub.providerId] = sub;
        }
        setSubscriptions(subMap);
      }
      if (pkgData.success) setPackages(pkgData.packages);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        // Expected during auth handoff or for non-clinic roles — silence.
        if (typeof console !== 'undefined' && console.debug) console.debug('[PackageEnforcement] non-fatal:', status);
      } else {
        console.error('Failed to fetch enforcement data:', e);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Get subscription for a provider
  const getProviderSubscription = useCallback((providerId) => {
    return subscriptions[providerId] || null;
  }, [subscriptions]);

  // Get package for a provider
  const getProviderPackage = useCallback((providerId) => {
    const sub = subscriptions[providerId];
    if (!sub) return null;
    return packages.find(p => p.id === sub.packageId) || null;
  }, [subscriptions, packages]);

  // Check if action is allowed (based on package limits).
  // PACKAGE-ENTITLEMENT-SSOT-REMEDIATION (2026-06): FAIL-CLOSED. Frontend is
  // UX-only; the backend EntitlementAuthority is authoritative. Unknown
  // actions/features NEVER become silent authorization grants, and a missing
  // package/subscription denies rather than allows.
  const canPerformAction = useCallback((providerId, action) => {
    const sub = subscriptions[providerId];
    if (!sub) return { allowed: false, reason: 'No subscription found' };
    if (sub.status !== 'active') return { allowed: false, reason: 'Subscription not active' };

    const pkg = packages.find(p => p.id === sub.packageId);
    if (!pkg) return { allowed: false, reason: 'Package not resolved' };

    const features = pkg.features || {};

    switch (action) {
      case 'add_user': {
        const maxUsers = (pkg.limits?.max_users ?? pkg.maxUsers);
        if (maxUsers === -1) return { allowed: true };
        if (maxUsers == null) return { allowed: false, reason: 'Seat limit unresolved' };
        const totalUsers = Object.values(sub.currentUsers || {}).reduce((a, b) => a + b, 0);
        if (totalUsers >= maxUsers) return { allowed: false, reason: `User limit reached (${maxUsers})` };
        return { allowed: true };
      }
      case 'receive_marketplace_lead': {
        const max = features.maxLeadsPerMonth ?? pkg.limits?.leads_monthly_max;
        if (max === -1) return { allowed: true };
        if (max == null) return { allowed: false, reason: 'Lead limit unresolved' };
        if ((sub.marketplaceLeadsThisMonth || 0) >= max) return { allowed: false, reason: `Monthly lead limit reached (${max})` };
        return { allowed: true };
      }
      case 'leads_enabled':
        return features.leads_enabled ? { allowed: true } : { allowed: false, reason: 'Leads not enabled on this plan' };
      case 'campaigns_enabled':
        return features.campaigns_enabled ? { allowed: true } : { allowed: false, reason: 'Campaigns not enabled on this plan' };
      default:
        // Unknown action/feature → deny (UX hint only; backend enforces).
        return { allowed: false, reason: 'Unknown action or feature' };
    }
  }, [subscriptions, packages]);

  // Update provider package
  const updateProviderPackage = useCallback(async (providerId, packageId) => {
    try {
      const pkg = packages.find(p => p.id === packageId);
      const name = pkg ? (typeof pkg.name === 'object' ? (pkg.name?.en || '') : pkg.name) : '';
      const res = await api.put(`/provider-enforcement/${providerId}/package?package_id=${packageId}&package_name=${encodeURIComponent(name)}`);
      const data = res.data;
      if (data.success) {
        setSubscriptions(prev => ({ ...prev, [providerId]: data.subscription }));
        return data.subscription;
      }
    } catch (e) { console.error('Failed to update package:', e); }
    return null;
  }, [packages]);

  // Increment user count
  const incrementUser = useCallback(async (providerId, userType) => {
    try {
      await api.put(`/provider-enforcement/${providerId}/increment-user?user_type=${userType}`);
      await fetchData();
    } catch (e) { console.error('Failed to increment user:', e); }
  }, [fetchData]);

  // Decrement user count
  const decrementUser = useCallback(async (providerId, userType) => {
    try {
      await api.put(`/provider-enforcement/${providerId}/decrement-user?user_type=${userType}`);
      await fetchData();
    } catch (e) { console.error('Failed to decrement user:', e); }
  }, [fetchData]);

  // Record marketplace lead
  const recordMarketplaceLead = useCallback(async (providerId) => {
    try {
      await api.put(`/provider-enforcement/${providerId}/increment-lead`);
      await fetchData();
    } catch (e) { console.error('Failed to record lead:', e); }
  }, [fetchData]);

  // Get usage stats for a provider
  const getUsageStats = useCallback((providerId) => {
    const sub = subscriptions[providerId];
    const pkg = packages.find(p => p.id === sub?.packageId);
    if (!sub || !pkg) return null;

    const totalUsers = Object.values(sub.currentUsers || {}).reduce((a, b) => a + b, 0);
    const maxLeads = pkg.features?.maxLeadsPerMonth || 0;

    return {
      currentUsers: totalUsers,
      maxUsers: pkg.maxUsers,
      userUtilization: pkg.maxUsers > 0 ? Math.round((totalUsers / pkg.maxUsers) * 100) : 0,
      marketplaceLeads: sub.marketplaceLeadsThisMonth || 0,
      maxLeadsPerMonth: maxLeads,
      leadUtilization: maxLeads > 0 ? Math.round(((sub.marketplaceLeadsThisMonth || 0) / maxLeads) * 100) : 0,
      packageName: pkg.name,
      status: sub.status,
    };
  }, [subscriptions, packages]);

  // Convenience wrappers expected by ClinicAdminDashboard
  const canAddUser = useCallback((providerId, userType) => {
    return canPerformAction(providerId, 'add_user');
  }, [canPerformAction]);

  const canReceiveMarketplaceLead = useCallback((providerId) => {
    return canPerformAction(providerId, 'receive_marketplace_lead');
  }, [canPerformAction]);

  const isFeatureAvailable = useCallback((providerId, featureName) => {
    return canPerformAction(providerId, featureName);
  }, [canPerformAction]);

  return (
    <PackageEnforcementContext.Provider value={{
      subscriptions,
      packages,
      loading,
      fetchData,
      getProviderSubscription,
      getProviderPackage,
      canPerformAction,
      canAddUser,
      canReceiveMarketplaceLead,
      isFeatureAvailable,
      updateProviderPackage,
      incrementUser,
      decrementUser,
      recordMarketplaceLead,
      getUsageStats,
    }}>
      {children}
    </PackageEnforcementContext.Provider>
  );
};

export default PackageEnforcementContext;
